import { useEffect, useState, type CSSProperties } from 'react'
import clsx from 'clsx'
import { useSearchParams } from 'react-router-dom'
import type { ConnectionsScenario } from '@app/shared'

import {
  useCaptureTokens,
  useClock,
  useConnectors,
  useDataStatus,
  useDesktopWorker,
  useSyncConnector,
} from '../lib/data'
import { CaptureTokens } from '../components/connections/CaptureTokens'
import { ConnectorCard } from '../components/connections/ConnectorCard'
import { DataAndAI } from '../components/connections/DataAndAI'
import { SettingsNav } from '../components/connections/SettingsNav'
import { WorkerCard } from '../components/connections/WorkerCard'
import { BuildBadge } from '../components/HeaderBand'
import { AlertCircle, Drive, Plus, Refresh } from '../components/connections/icons'
import { bytesPhrase } from '../components/connections/labels'
import { readMailResult, withoutMailResult, type MailResult } from '../components/connections/mail'
import {
  headerCounts,
  pageBanner,
  sectionOrder,
  worstTone,
  type PageBanner as Banner,
} from '../components/connections/model'
import {
  ActionButton,
  PanelSkeleton,
  PanelUnavailable,
  Section,
} from '../components/connections/parts'

// ============================================================================
// CONNECTIONS — docs/design/handoff/"LifeOS Connections.dc.html", 12a and 12b.
//
// THE TRUST SURFACE. Every other screen in LifeOS is allowed to show what it
// knows; this one has to show what it does NOT know, and why, and since when.
// Three rules run through all of it:
//
//   1. NAME THE CONSEQUENCE, NOT THE ERROR. "Canvas token expired" is a fact
//      about a token. "assignment due dates and grades on the School screen are
//      from Sep 13" is a fact about the owner's week — and it is the one he
//      needs. Every error strip on this screen is written the second way.
//   2. OFFER EXACTLY ONE ACTION, AND MAKE IT ONE THAT HELPS — or offer none. A
//      rate limit gets no button because pressing one would only make it worse.
//      A broken connector gets `Try again`, wired to the real sync, because the
//      owner does not own a terminal: an action he cannot take is not an action.
//   3. NOTHING PRETENDS. A planned connector is dashed and inert. A sleeping PC
//      lists the jobs it will run when it wakes. A stale token's Revoke gets
//      louder, not quieter. The actions whose backends genuinely do not exist yet
//      (Sync all, Add connector, Disconnect, Switch to real data) carry the
//      sentence naming what they wait on instead of a click that goes nowhere.
//
// LAYOUT: rail 62 (the shell's) · settings nav 236 · main scroll.
// ============================================================================

/** Review-only: the mockup option ids, mapped to the states the demo data models. */
const SCENARIOS: Record<string, ConnectionsScenario> = {
  '12a': 'connector_broken',
  '12b': 'worker_offline',
}

export function Connections() {
  const [params, setParams] = useSearchParams()
  const scenario = SCENARIOS[params.get('state') ?? '']

  // GOOGLE SIGN-IN COMES BACK HERE with `?mail=connected|error`. Read it ONCE into state and take
  // it out of the URL, so a reload or a shared link does not announce a connection twice.
  const [mailResult, setMailResult] = useState<MailResult | null>(null)
  const mailParam = params.get('mail')
  useEffect(() => {
    if (!mailParam) return
    setMailResult(readMailResult(params))
    setParams(withoutMailResult(params), { replace: true })
  }, [mailParam, params, setParams])

  const { data: clock } = useClock()
  const connectors = useConnectors(scenario)
  const worker = useDesktopWorker(scenario)
  const tokens = useCaptureTokens(scenario)
  const dataStatus = useDataStatus(scenario)

  const tone = worstTone(connectors.data, worker.data, dataStatus.data)
  const banner =
    clock && pageBanner(connectors.data ?? [], dataStatus.data, clock.date, clock.minutesIntoDay)
  const order = sectionOrder(connectors.data, worker.data)

  const sources = (
    <Section label="Sources">
      {connectors.isPending && <PanelSkeleton height={260} />}
      {connectors.error && <PanelUnavailable error={connectors.error} />}
      {connectors.data && clock && (
        <div className="grid grid-cols-2 gap-3">
          {connectors.data.map((connector) => (
            <ConnectorCard
              key={connector.id}
              connector={connector}
              today={clock.date}
              nowMinutes={clock.minutesIntoDay}
              mailResult={mailResult}
            />
          ))}
        </div>
      )}
    </Section>
  )

  const machine = (
    <Section label="Your machine">
      {worker.isPending && <PanelSkeleton />}
      {worker.error && <PanelUnavailable error={worker.error} />}
      {worker.data && clock && (
        <WorkerCard worker={worker.data} today={clock.date} nowMinutes={clock.minutesIntoDay} />
      )}
    </Section>
  )

  return (
    <div className="flex min-h-0 flex-1">
      <SettingsNav
        tone={tone}
        toneReason={toneReason(tone)}
        demoMode={dataStatus.data?.mode === 'demo'}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="flex flex-none items-center gap-[14px] border-b border-line bg-surface px-[30px] pb-4 pt-5">
          <div className="flex flex-col gap-[5px]">
            <h1 className="text-[16px] font-semibold leading-none tracking-[-0.01em]">Connections</h1>
            {/* No placeholder count while it loads: a made-up "0 connected" is
                the exact claim this screen exists never to make. */}
            {connectors.data && (
              <p className="text-[11.5px] font-normal leading-none text-microcopy">
                {headerCounts(connectors.data, worker.data)}
              </p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <BuildBadge className="mr-1" />
            <ActionButton
              label="Sync all"
              variant="quiet"
              size="md"
              icon={<Refresh size={12} className="text-muted" />}
              waitingFor="There is one live connector so far, and its own card syncs it. This button earns its place when there are two."
            />
            <ActionButton
              label="Add connector"
              variant="dark"
              size="md"
              icon={<Plus size={12} className="text-chat-lift" />}
              waitingFor="Adding a connector means writing a SourceAdapter and deploying it — the layer exists, a self-service flow for a single-user app does not."
            />
          </div>
        </header>

        <div className="flex flex-col gap-5 px-[30px] pb-[30px] pt-[18px]">
          {banner && <PageBannerStrip banner={banner} />}

          {order.map((section) => (
            <div key={section}>{section === 'sources' ? sources : machine}</div>
          ))}

          <div className="flex items-stretch gap-3">
            <Section label="Capture tokens" className="min-w-0 flex-1">
              {tokens.isPending && <PanelSkeleton />}
              {tokens.error && <PanelUnavailable error={tokens.error} />}
              {tokens.data && clock && (
                <CaptureTokens
                  tokens={tokens.data}
                  today={clock.date}
                  nowMinutes={clock.minutesIntoDay}
                />
              )}
            </Section>

            <Section label="Data & AI" className="w-[462px] flex-none">
              {dataStatus.isPending && <PanelSkeleton />}
              {dataStatus.error && <PanelUnavailable error={dataStatus.error} />}
              {dataStatus.data && clock && (
                <DataAndAI
                  status={dataStatus.data}
                  today={clock.date}
                  nowMinutes={clock.minutesIntoDay}
                />
              )}
            </Section>
          </div>

          {/* Pinned last: where the bytes actually are. Absent rather than
              invented when the worker cannot be read. */}
          {worker.data && (
            <div className="flex items-center gap-[10px] rounded-row border border-line bg-surface px-[15px] py-[11px]">
              <Drive size={14} strokeWidth={1.8} className="flex-none text-muted" />
              <p className="text-[11.5px] font-normal leading-[1.4] text-ink-3">
                Lecture audio: archived on your PC · transcripts in cloud
              </p>
              <p className="ml-auto text-[11px] font-normal leading-none text-microcopy">
                {bytesPhrase(worker.data.storage.archivedBytes)} on {worker.data.storage.machine} ·{' '}
                {bytesPhrase(worker.data.storage.syncedBytes)} synced
              </p>
            </div>
          )}
        </div>
      </div>

      <ReviewStateSwitch
        active={params.get('state') ?? ''}
        onPick={(id) => setParams(id ? { state: id } : {}, { replace: true })}
      />
    </div>
  )
}

function toneReason(tone: ReturnType<typeof worstTone>): string | null {
  if (tone === 'danger') return 'A connector is broken.'
  if (tone === 'warning') return 'Something is degraded or showing sample data.'
  return null
}

/**
 * ONE BANNER, and it is the first thing on the page because it is the only thing
 * that changes what the owner should do next. Its sentence is built in
 * model.ts (`pageBanner`), not written here — the phrasing IS the product's
 * voice and it belongs somewhere a test can read it.
 *
 * THE BANNER'S BUTTON IS THE CARD'S BUTTON. It runs the same `syncConnector`
 * mutation the broken connector's own `Try again` runs, on the id `pageBanner`
 * hands over — because a banner that names a problem and then offers a control
 * that does nothing is worse than a banner with no control at all, and that is
 * the exact thing the owner reported. The demo banner keeps no click: its
 * "action" is a build's configuration, and saying so on hover is the honest
 * answer there.
 */
function PageBannerStrip({ banner }: { banner: Banner }) {
  const danger = banner.tone === 'danger'
  const retryId = banner.actionConnectorId
  const sync = useSyncConnector()
  const busy = retryId !== null && sync.isPending
  return (
    <div
      role="status"
      className={clsx(
        'flex flex-col gap-[9px] rounded-banner border px-4 py-3',
        danger ? 'border-danger-line bg-danger-fill' : 'border-warning-line bg-warning-fill',
      )}
    >
      <div className="flex items-center gap-[11px]">
        <AlertCircle
          size={15}
          className={clsx('flex-none', danger ? 'text-danger-2' : 'text-warning-2')}
        />
        <p
          className={clsx(
            'min-w-0 flex-1 text-[12.5px] font-medium leading-[1.4] text-pretty',
            danger ? 'text-danger' : 'text-warning',
          )}
        >
          {banner.text}
        </p>
        <ActionButton
          label={busy ? 'Trying again…' : banner.actionLabel}
          variant={danger ? 'danger' : 'warning'}
          size="md"
          busy={busy}
          onClick={retryId === null ? undefined : () => sync.mutate(retryId)}
          waitingFor={
            retryId === null
              ? "Which dataset the app runs on is set by this build's configuration, not by a button. A runtime switch is not built."
              : undefined
          }
        />
      </div>

      {/* THE RETRY THAT FAILED SAYS SO, IN WORDS, HERE. The banner started the sync, so the
          banner owes the answer — sending the owner to hunt for the card's error strip would be
          the same dead end in a different place, and a button that just stops spinning is a
          silent failure. */}
      {retryId !== null && sync.error && (
        <p className="pl-[26px] text-[11.5px] font-normal leading-[1.4] text-danger text-pretty">
          {sync.error instanceof Error
            ? sync.error.message
            : 'That retry could not be started, and the reason did not come back with a sentence attached.'}
        </p>
      )}
    </div>
  )
}

/**
 * THE REVIEW SWITCH — development builds only, and it never ships.
 *
 * Both designed states have to be reachable to be reviewed, and a demo build
 * cannot break a connector on request. `?state=12a` / `?state=12b` selects one;
 * this chip is only a way to type that without typing it. Production builds drop
 * it entirely (the `import.meta.env.DEV` branch is removed at build time), so the
 * screenshot pair is photographed from the built app with the query parameter,
 * with nothing extra in the frame.
 */
function ReviewStateSwitch({
  active,
  onPick,
}: {
  active: string
  onPick: (id: string) => void
}) {
  if (!import.meta.env.DEV) return null
  return (
    <div className="fixed bottom-3 right-3 z-10 flex items-center gap-1 rounded-pill border border-line bg-surface px-2 py-[5px] shadow-card">
      <span className="px-1 text-[9.5px] font-semibold uppercase tracking-[0.08em] text-microcopy">
        review
      </span>
      {['12a', '12b'].map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onPick(active === id ? '' : id)}
          style={
            { '--mo-rule': active === id ? 'var(--color-surface)' : 'var(--color-ink)' } as CSSProperties
          }
          className={clsx(
            'mo-ink mo-press rounded-pill px-2 py-1 text-[10px] font-semibold leading-none',
            active === id ? 'bg-ink text-surface' : 'text-ink-2',
          )}
        >
          {id}
        </button>
      ))}
    </div>
  )
}
